import {
    Listing,
    PriceSnapshot,
    PriceSnapshotDailyLowest,
    PriceSnapshotLowestPoint,
    PriceSnapshotRangeSummary
} from './domain';
import {
    lookitPriceSnapshots
} from './mock-data';
import { mockPlatformAdapterRegistry } from './mock-platform-adapter';
import { getPublicPriceBreakdown, PublicPriceBreakdown } from './price-utils';
import {
    createInMemoryPriceSnapshotRepository,
    LowestPriceByRangeResult,
    PriceSnapshotRangeQuery,
    PriceSnapshotRepository,
    PriceSnapshotWriteInput
} from './price-snapshot-repository';

export interface ListingQuery {
    productClusterId?: string;
    listingIds?: string[];
}

export interface PriceSnapshotQuery {
    productClusterId?: string;
    listingIds?: string[];
}

export interface PricedListing extends Listing {
    price_breakdown: PublicPriceBreakdown;
    public_benefits: Listing['benefits'];
    personalized_benefits: Listing['benefits'];
    excluded_benefits: Listing['benefits'];
}

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function attachListingPricing(listing: Listing): PricedListing {
    const priceBreakdown = getPublicPriceBreakdown(listing);

    return {
        ...listing,
        sale_price: priceBreakdown.sale_price,
        shipping_fee: priceBreakdown.shipping_fee,
        public_effective_price: priceBreakdown.public_effective_price,
        price_breakdown: priceBreakdown,
        public_benefits: priceBreakdown.publicBenefit,
        personalized_benefits: priceBreakdown.personalizedBenefit,
        excluded_benefits: priceBreakdown.excludedBenefit
    };
}

function attachSnapshotPricing(snapshot: PriceSnapshot): PriceSnapshot {
    const priceBreakdown = getPublicPriceBreakdown(snapshot);

    return {
        ...snapshot,
        effectivePrice: priceBreakdown.public_effective_price,
        basePrice: priceBreakdown.basePrice,
        shippingFee: priceBreakdown.shipping_fee,
        couponApplied: priceBreakdown.publicCoupon > 0,
        listed_price: priceBreakdown.basePrice,
        instant_discount: priceBreakdown.instantDiscount,
        public_coupon_discount: priceBreakdown.publicCoupon,
        shipping_fee: priceBreakdown.shipping_fee,
        public_effective_price: priceBreakdown.public_effective_price
    };
}

function attachLowestPointPricing<T extends PriceSnapshotLowestPoint | null>(point: T): T {
    if (!point) return point;

    const snapshot = attachSnapshotPricing(point.snapshot);
    return {
        ...point,
        snapshot,
        effectivePrice: snapshot.effectivePrice,
        checkedAt: snapshot.checkedAt,
        collectedAt: snapshot.collectedAt,
        public_effective_price: snapshot.effectivePrice,
        checked_at: snapshot.checkedAt,
        collected_at: snapshot.collectedAt
    } as T;
}

function attachDailyLowestPricing(point: PriceSnapshotDailyLowest): PriceSnapshotDailyLowest {
    const pricedPoint = attachLowestPointPricing(point);

    return {
        ...pricedPoint,
        date_key: pricedPoint.dateKey,
        source_count: pricedPoint.sourceCount
    };
}

function attachPriceTrendPricing(summary: PriceSnapshotRangeSummary): PriceSnapshotRangeSummary {
    const snapshots = summary.snapshots.map((snapshot) => attachSnapshotPricing(snapshot));
    const dailyLowest = summary.dailyLowest.map((point) => attachDailyLowestPricing(point));
    const overallLowest = attachLowestPointPricing(summary.overallLowest);

    return {
        ...summary,
        snapshots,
        dailyLowest,
        overallLowest,
        daily_lowest: dailyLowest,
        overall_lowest: overallLowest,
        hasData: dailyLowest.length > 0,
        has_data: dailyLowest.length > 0
    };
}

export const priceSnapshotRepository: PriceSnapshotRepository = createInMemoryPriceSnapshotRepository(lookitPriceSnapshots);
let activePriceSnapshotRepository: PriceSnapshotRepository = priceSnapshotRepository;

export function usePriceSnapshotRepository(repository: PriceSnapshotRepository): PriceSnapshotRepository {
    activePriceSnapshotRepository = repository;
    return activePriceSnapshotRepository;
}

export function resetPriceSnapshotRepository(): PriceSnapshotRepository {
    activePriceSnapshotRepository = priceSnapshotRepository;
    return activePriceSnapshotRepository;
}

export function getActivePriceSnapshotRepository(): PriceSnapshotRepository {
    return activePriceSnapshotRepository;
}

export async function getListings(query: ListingQuery = {}): Promise<PricedListing[]> {
    const listings = await mockPlatformAdapterRegistry.searchListings({
        productClusterIds: query.productClusterId ? [query.productClusterId] : undefined,
        listingIds: query.listingIds,
        includeSoldOut: true
    });

    return cloneData(listings.map((listing) => attachListingPricing(listing)));
}

export async function getListingsByProductCluster(productClusterId: string): Promise<PricedListing[]> {
    return getListings({ productClusterId });
}

export async function getListing(listingId: string): Promise<PricedListing | null> {
    const listings = await mockPlatformAdapterRegistry.searchListings({ listingIds: [listingId], includeSoldOut: true });
    return listings[0] ? cloneData(attachListingPricing(listings[0])) : null;
}

export async function getPriceSnapshots(query: PriceSnapshotQuery = {}): Promise<PriceSnapshot[]> {
    const listingIds = query.productClusterId
        ? new Set((await getListings({ productClusterId: query.productClusterId })).map((listing) => listing.id))
        : query.listingIds
            ? new Set(query.listingIds)
            : null;
    const snapshots = await getActivePriceSnapshotRepository().getSnapshotsByRange({
        productClusterId: query.productClusterId,
        listingIds: listingIds ? Array.from(listingIds) : query.listingIds
    });

    return cloneData(snapshots.map((snapshot) => attachSnapshotPricing(snapshot)));
}

export async function getPriceSnapshotsByProductCluster(productClusterId: string): Promise<PriceSnapshot[]> {
    return getPriceSnapshots({ productClusterId });
}

export async function saveSnapshot(snapshot: PriceSnapshotWriteInput): Promise<PriceSnapshot> {
    return getActivePriceSnapshotRepository().saveSnapshot(snapshot);
}

export async function getSnapshotsByRange(query: PriceSnapshotRangeQuery): Promise<PriceSnapshot[]> {
    const snapshots = await getActivePriceSnapshotRepository().getSnapshotsByRange(query);
    return cloneData(snapshots.map((snapshot) => attachSnapshotPricing(snapshot)));
}

export async function getLowestPriceByRange(query: PriceSnapshotRangeQuery): Promise<LowestPriceByRangeResult | null> {
    const result = await getActivePriceSnapshotRepository().getLowestPriceByRange(query);
    if (!result) return null;
    return attachLowestPointPricing(result) as LowestPriceByRangeResult;
}

export async function getPriceTrendByRange(query: PriceSnapshotRangeQuery): Promise<PriceSnapshotRangeSummary> {
    const summary = await getActivePriceSnapshotRepository().getPriceTrendByRange(query);
    return cloneData(attachPriceTrendPricing(summary));
}

export const priceService = {
    priceSnapshotRepository,
    getActivePriceSnapshotRepository,
    usePriceSnapshotRepository,
    resetPriceSnapshotRepository,
    getListings,
    getListingsByProductCluster,
    getListing,
    getPriceSnapshots,
    getPriceSnapshotsByProductCluster,
    saveSnapshot,
    getSnapshotsByRange,
    getLowestPriceByRange,
    getPriceTrendByRange
};
